import React, { useCallback } from 'react';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { ff } from '@atlassian/jira-feature-flagging';
import { componentWithFF } from '@atlassian/jira-feature-flagging-utils';
import {
	InlineEditLite,
	type EditViewFieldProps,
	type ReadViewContainerProps,
	type ReadViewEditButtonProps,
	useInlineEdit,
} from '@atlassian/jira-inline-edit-lite';

import { useIntl } from '@atlassian/jira-intl';
import { InlineEditReadViewContainerOld } from '@atlassian/jira-issue-field-inline-edit-read-view-container';
import ValidationPopup, {
	type ValidationPopupProps,
} from '@atlassian/jira-issue-field-validation-popup';

import { EditView } from './edit-view';
import messages from './messages';
import { ReadView } from './read-view';
import { ReadViewNonEditable } from './read-view-non-editable';
import type { FieldInlineEditLiteProps, ValidationFieldProps } from './types';

// Fallback placements to be used for the validation popover if it overflows its clipping parent
const fallbackPlacements: ValidationPopupProps['fallbackPlacements'] = [
	'left',
	'top-start',
	'auto',
];

/**
 * Wrapper component around `InlineEditLite` which provides consistent spacing and validation UX for issue fields.
 */
const FieldInlineEditLiteOld = ({
	editView,
	fieldName,
	hasUnsubmittedChanges,
	hideEditButton,
	invalidMessage,
	isEditable,
	onConfirm,
	readViewFitContainerHeight,
	readViewContainerRef,
	readView,
	confirmButtonLabel: confirmButtonLabelProp,
	cancelButtonLabel: cancelButtonLabelProp,
	editButtonLabel: editButtonLabelProp,
	...inlineEditLiteProps
}: FieldInlineEditLiteProps) => {
	const { formatMessage } = useIntl();

	const renderReadView = (props?: {
		containerProps: ReadViewContainerProps;
		editButtonProps: ReadViewEditButtonProps;
	}) => (
		<InlineEditReadViewContainerOld
			containerProps={props?.containerProps}
			editButtonProps={props?.editButtonProps}
			hideEditButton={hideEditButton}
			ref={readViewContainerRef}
			// Highlight the field as invalid to notify the user if there was an error attempting to submit changes.
			isInvalid={hasUnsubmittedChanges}
			readViewFitContainerHeight={readViewFitContainerHeight}
		>
			{readView()}
		</InlineEditReadViewContainerOld>
	);

	const renderEditView = ({ id }: EditViewFieldProps<unknown>) => (
		<ValidationPopup
			fieldId={id}
			message={invalidMessage}
			trigger={(triggerProps) => editView(triggerProps)}
			placement="right"
			fallbackPlacements={fallbackPlacements}
		/>
	);

	const handleConfirm = useCallback(
		(_: unknown, analyticsEvent: UIAnalyticsEvent) => onConfirm(analyticsEvent),
		[onConfirm],
	);

	if (!isEditable) {
		return renderReadView();
	}

	let confirmButtonLabel = confirmButtonLabelProp;
	let cancelButtonLabel = cancelButtonLabelProp;
	let editButtonLabel = editButtonLabelProp;

	if (ff('nin-priority-inline-edit')) {
		confirmButtonLabel =
			confirmButtonLabelProp ??
			formatMessage(messages.confirmButtonLabel, {
				fieldName,
			});
		cancelButtonLabel =
			cancelButtonLabelProp ??
			formatMessage(messages.cancelButtonLabel, {
				fieldName,
			});
		editButtonLabel =
			editButtonLabelProp ??
			formatMessage(messages.editButtonLabel, {
				fieldName,
			});
	}

	return (
		<InlineEditLite
			{...inlineEditLiteProps}
			editView={renderEditView}
			onConfirm={handleConfirm}
			readView={renderReadView}
			confirmButtonLabel={confirmButtonLabel}
			cancelButtonLabel={cancelButtonLabel}
			editButtonLabel={editButtonLabel}
		/>
	);
};

/**
 * Component leveraging `@atlassian/jira-inline-edit-lite` to provide consistent spacing and validation UX for issue
 * fields.
 */
const FieldInlineEditLite = ({
	editView,
	editViewPopup,
	editViewPopupAlignBlock,
	editViewPopupMinWidth,
	fieldName,
	hasUnsubmittedChanges,
	hideActionButtons,
	hideEditButton,
	invalidMessage,
	isEditable,
	isEditing,
	keepEditViewOpenOnBlur,
	onCancel,
	onConfirm,
	onEdit,
	readViewFitContainerHeight,
	readViewContainerRef,
	readView,
	confirmButtonLabel: confirmButtonLabelProp,
	cancelButtonLabel: cancelButtonLabelProp,
	editButtonLabel: editButtonLabelProp,
}: FieldInlineEditLiteProps) => {
	const { formatMessage } = useIntl();

	const { context, refs } = useInlineEdit({
		isEditing,
		onCancel,
		onConfirm,
		onEdit,
	});

	const confirmButtonLabel =
		confirmButtonLabelProp ??
		formatMessage(messages.confirmButtonLabel, {
			fieldName,
		});
	const cancelButtonLabel =
		cancelButtonLabelProp ??
		formatMessage(messages.cancelButtonLabel, {
			fieldName,
		});
	const editButtonLabel =
		editButtonLabelProp ??
		formatMessage(messages.editButtonLabel, {
			fieldName,
		});

	if (!isEditable) {
		return (
			<ReadViewNonEditable
				hideEditButton={hideEditButton}
				readViewFitContainerHeight={readViewFitContainerHeight}
			>
				{readView()}
			</ReadViewNonEditable>
		);
	}

	if (isEditing) {
		return (
			<EditView
				cancelButtonLabel={cancelButtonLabel}
				confirmButtonLabel={confirmButtonLabel}
				context={context}
				editViewPopup={editViewPopup}
				editViewPopupAlignBlock={editViewPopupAlignBlock}
				editViewPopupMinWidth={editViewPopupMinWidth}
				hideActionButtons={hideActionButtons}
				keepEditViewOpenOnBlur={keepEditViewOpenOnBlur}
			>
				<ValidationPopup
					message={invalidMessage}
					trigger={(triggerProps: ValidationFieldProps) => editView(triggerProps)}
					placement="right"
					fallbackPlacements={fallbackPlacements}
				/>
			</EditView>
		);
	}

	return (
		<ReadView
			context={context}
			editButtonLabel={editButtonLabel}
			editButtonRef={refs.editButton}
			hideEditButton={hideEditButton}
			isInvalid={hasUnsubmittedChanges}
			readViewFitContainerHeight={readViewFitContainerHeight}
			readViewContainerRef={readViewContainerRef}
		>
			{readView()}
		</ReadView>
	);
};

export default componentWithFF(
	'one-issue-table-inline-edit-lite-api-changes',
	FieldInlineEditLite,
	FieldInlineEditLiteOld,
);

export { ReadViewNonEditable } from './read-view-non-editable';
